import { useEffect, useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  Car,
  Check,
  Flag,
  MapPin,
  MessageCircle,
  PawPrint,
  Share2,
  ShieldCheck,
  Star,
  Video,
} from "lucide-react";
import { WHATSAPP_NUMBER } from "@/lib/petmoon";
import { cn } from "@/lib/utils";
import { TrackingMap } from "./TrackingMap";
import maxAvatar from "@/assets/max-avatar.jpg";
import logoAsset from "@/assets/petmoon-logo.png.asset.json";

const stages = [
  { label: "Recogiendo", icon: MapPin },
  { label: "En Tránsito", icon: Car },
  { label: "Llegando", icon: Flag },
] as const;

const currentStage = 1;

const driverMessage = encodeURIComponent(
  "Hola Geo, soy la familia de Max 🐾 (viaje PetMoon #PM-2847). ¿Cómo va todo?",
);

export function ActiveRideTracking() {
  const { mascota } = useSearch({ from: "/seguimiento" });
  const petName = mascota?.trim() || "Max";
  const [showCamera, setShowCamera] = useState(false);
  const [secondsAgo, setSecondsAgo] = useState(4);
  const [copied, setCopied] = useState(false);

  const shareText = `¡Sigue el viaje de ${petName} en PetMoon aquí! 🐾🌙`;

  const shareUrl =
    typeof window === "undefined" ? "" : window.location.href;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  useEffect(() => {
    const id = window.setInterval(() => {
      setSecondsAgo((s) => (s >= 59 ? 0 : s + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background font-sans text-foreground antialiased">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2.5"
            aria-label="PetMoon — volver al inicio"
          >
            <img
              src={logoAsset.url}
              alt=""
              className="h-9 w-9 shrink-0 rounded-full"
              width={36}
              height={36}
            />
            <span className="truncate font-display text-lg font-bold tracking-tight text-primary">
              PetMoon
            </span>
          </Link>

          <span className="hidden items-center gap-2 rounded-full bg-night px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-widest text-night-foreground sm:inline-flex">
            <span className="relative flex h-2 w-2" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            En vivo
          </span>

          <Link
            to="/"
            className="inline-flex shrink-0 items-center gap-1.5 text-sm font-extrabold text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Inicio
          </Link>
        </div>
      </header>

      <main className="flex flex-1 flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_460px]">
        {/* Map */}
        <div className="relative min-h-[46vh] flex-1 lg:min-h-0">
          <TrackingMap />

          <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-night/85 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-widest text-night-foreground shadow-soft backdrop-blur-sm sm:hidden">
            <span className="relative flex h-2 w-2" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            En vivo
          </span>

          <p className="absolute right-4 top-4 max-w-[60%] truncate rounded-full bg-card/90 px-4 py-2 text-xs font-extrabold text-foreground shadow-soft ring-1 ring-border backdrop-blur-sm">
            Zapopan → Guadalajara
          </p>
        </div>

        {/* Status card */}
        <section
          aria-label="Estado del viaje"
          className="relative z-10 -mt-8 rounded-t-[2rem] bg-card px-5 pb-10 pt-7 shadow-card ring-1 ring-border sm:px-8 lg:mt-0 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto lg:rounded-none lg:border-l lg:border-border lg:shadow-none lg:ring-0"
        >
          {/* Pet header */}
          <div className="flex items-center gap-4">
            <img
              src={maxAvatar}
              alt="Foto de Max, un perrito beagle sonriente"
              className="h-16 w-16 shrink-0 rounded-full object-cover ring-4 ring-accent/50"
              width={512}
              height={512}
              loading="lazy"
              decoding="async"
            />

            <div className="min-w-0">
              <h1 className="truncate font-display text-2xl font-bold text-foreground">
                {petName} en camino 🐾
              </h1>
              <p className="text-sm font-semibold text-muted-foreground">
                Viaje #PM-2847 · Transporte PetMoon
              </p>
            </div>
          </div>

          {/* ETA */}
          <div className="mt-6 rounded-2xl bg-primary px-5 py-5 text-primary-foreground">
            <p className="text-xs font-bold uppercase tracking-widest text-primary-foreground/70">
              Llegada estimada
            </p>
            <p className="mt-1 font-display text-4xl font-bold">
              15 min{" "}
              <span className="text-lg font-bold text-accent">· 2:45 PM</span>
            </p>
            <p
              className="mt-2 text-xs font-semibold text-primary-foreground/70"
              aria-live="polite"
            >
              Actualizado hace {secondsAgo} s
            </p>
          </div>

          {/* Progress */}
          <ol
            className="mt-7 flex items-center"
            aria-label="Progreso del viaje"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={3}
            aria-valuenow={currentStage + 1}
            aria-valuetext="En Tránsito"
          >
            {stages.map((stage, i) => {
              const done = i < currentStage;
              const current = i === currentStage;
              return (
                <li key={stage.label} className={cn("flex items-center", i > 0 && "flex-1")}>
                  {i > 0 && (
                    <span
                      aria-hidden
                      className={cn(
                        "mx-2 h-1 flex-1 rounded-full",
                        i <= currentStage ? "bg-accent" : "bg-border",
                      )}
                    />
                  )}
                  <div
                    className="flex flex-col items-center gap-1.5"
                    aria-current={current ? "step" : undefined}
                  >
                    <span
                      className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-full transition-colors",
                        done && "bg-accent text-accent-foreground",
                        current && "bg-primary text-primary-foreground ring-4 ring-accent/40",
                        !done && !current && "bg-secondary text-muted-foreground",
                      )}
                    >
                      {done ? (
                        <Check className="h-5 w-5" aria-hidden />
                      ) : (
                        <stage.icon className="h-5 w-5" aria-hidden />
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-[11px] font-bold sm:text-xs",
                        current ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {stage.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
          <p className="mt-3 text-center text-sm font-bold text-primary" role="status">
            Tu peludo está en tránsito, todo va perfecto ✨
          </p>

          {/* Reassurance */}
          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-gold-soft px-4 py-3.5">
            <PawPrint className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep" aria-hidden />
            <p className="text-sm font-semibold text-foreground">
              {petName} viaja con arnés de seguridad, agua fresca y su mantita favorita. 🧡
            </p>
          </div>

          {/* Driver */}
          <div className="mt-6 rounded-2xl p-4 ring-1 ring-border">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Tu conductor
            </p>
            <div className="mt-3 flex items-center gap-3.5">
              <span
                aria-hidden
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary font-display text-base font-bold text-primary-foreground"
              >
                G
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-bold text-foreground">
                  Geo
                </p>
                <p className="flex items-center gap-1 text-xs font-bold text-muted-foreground">
                  <Star className="h-3.5 w-3.5 fill-accent text-accent" aria-hidden />
                  4.9 ·
                  <ShieldCheck className="h-3.5 w-3.5 text-whatsapp" aria-hidden />
                  Conductor verificado
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-xs font-bold text-foreground">
                  <Car className="h-3.5 w-3.5 text-primary" aria-hidden />
                  Nissan Versa blanco ·{" "}
                  <span className="rounded-md bg-secondary px-1.5 py-0.5 tracking-widest">
                    JHT-472-A
                  </span>
                </p>
              </div>
            </div>
            <p className="mt-3 rounded-xl bg-whatsapp/10 px-3 py-2 text-xs font-extrabold text-whatsapp">
              Viaje confirmado · tu unidad va en camino
            </p>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${driverMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-whatsapp px-5 py-3 text-sm font-extrabold text-whatsapp-foreground shadow-soft transition-all hover:-translate-y-0.5 hover:brightness-105"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              Contactar al Conductor
            </a>
          </div>

          {/* Compartir estado del viaje */}
          <div className="mt-6 rounded-2xl p-4 ring-1 ring-border">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              <Share2 className="h-3.5 w-3.5" aria-hidden />
              Compartir estado del viaje
            </p>
            <p className="mt-2 text-sm font-semibold text-muted-foreground">
              Manda la actualización a tu familia o a la veterinaria.
            </p>
            <div className="mt-3.5 grid gap-2.5 sm:grid-cols-2">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full bg-whatsapp px-5 py-3 text-sm font-extrabold text-whatsapp-foreground shadow-soft transition-all hover:-translate-y-0.5 hover:brightness-105"
              >
                <MessageCircle className="h-4 w-4" aria-hidden />
                Enviar por WhatsApp
              </a>
              <button
                type="button"
                onClick={() => void copyShare()}
                className="flex items-center justify-center gap-2 rounded-full bg-secondary px-5 py-3 text-sm font-extrabold text-primary transition-all hover:-translate-y-0.5"
              >
                {copied ? (
                  <Check className="h-4 w-4" aria-hidden />
                ) : (
                  <Share2 className="h-4 w-4" aria-hidden />
                )}
                {copied ? "¡Enlace copiado!" : "Copiar enlace"}
              </button>
            </div>
          </div>

          {/* Live camera teaser */}
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowCamera((v) => !v)}
              aria-expanded={showCamera}
              className="flex w-full items-center justify-between gap-3 rounded-2xl bg-night px-5 py-4 text-left shadow-soft transition-all hover:-translate-y-0.5"
            >
              <span className="flex items-center gap-3">
                <Video className="h-5 w-5 shrink-0 text-accent" aria-hidden />
                <span className="font-display text-base font-bold text-night-foreground">
                  Ver Cámara a Bordo
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-accent px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest text-accent-foreground">
                Premium
              </span>
            </button>
            {showCamera && (
              <div className="mt-3 animate-step-in overflow-hidden rounded-2xl ring-1 ring-border">
                <div className="flex aspect-video flex-col items-center justify-center gap-2.5 bg-night px-6 text-center">
                  <Video className="h-8 w-8 text-accent" aria-hidden />
                  <p className="font-display text-base font-bold text-night-foreground">
                    Cámara a bordo
                  </p>
                  <p className="max-w-xs text-xs font-semibold leading-relaxed text-night-foreground/70">
                    Muy pronto podrás ver a tu peludo en vivo durante todo el trayecto,
                    exclusivo de los viajes PetMoon Premium.
                  </p>
                </div>
              </div>
            )}
          </div>

          <p className="mt-8 text-center text-xs font-semibold text-muted-foreground">
            PetMoon · El viaje que tu mascota merece 🌙
          </p>
        </section>
      </main>
    </div>
  );
}
